import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragOverEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ProfileModuleType,
  type ProfileModuleDto,
  type ProfileTemplateFieldMode,
} from '@up2stay/up2promo-api-client';
import type { IconList, IconName } from '@up2stay/up2promo-ui';
import {
  Button,
  ConfirmDialog,
  getSocialCustomIconPickerList,
  Icon,
  IconPicker,
  iconPickerI18nDe,
  InlineEditCard,
  Input,
  Label,
  SocialLinkIcon,
} from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import { ExternalLink, GripVertical, Plus, Trash2 } from 'lucide-react';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';

import type { ModuleEntryGovernanceProps } from '@/core/components/profile/module-entry-governance';
import {
  ModuleEntryListRowBody,
  ModuleEntryOverrideHint,
  ModuleEntryPolicySlot,
} from '@/core/components/profile/module-entry-governance-ui';
import { ProfileFieldLockedHint } from '@/core/components/profile-templates/profile-field-locked-hint';
import { isIndividualFieldMode } from '@/core/components/profile-templates/template-member-value-inputs';
import { useDndProfileSensors } from '@/core/hooks/use-dnd-profile-sensors';
import { useIsMobile } from '@/core/hooks/use-mobile';
import {
  lockBodyScrollDuringDrag,
  triggerDragEndHaptic,
  triggerDragOverHaptic,
  triggerDragStartHaptic,
  unlockBodyScrollAfterDrag,
} from '@/core/lib/dnd-haptics';
import {
  defaultPolicyForNewEntry,
  isEntryPolicyLocked,
  resolveEntryPolicy,
} from '@/core/lib/profile-module-entry-policies';
import { openExternalUrl } from '@/core/lib/open-external-url';
import {
  getSocialHandlePrefix,
  getSocialIcon,
  SOCIAL_ICONS,
  getSocialUrl,
  isValidSocialHandle,
  normalizeSocialHandle,
  getSocialUsername,
  parseCustomSocialIconName,
} from '@/core/lib/socials';
import {
  SocialPlatform,
  type SocialLink,
  type SocialsModuleConfig,
} from '@/core/types/profile-modules';
import {
  SocialEditForm,
  getEmptyFormState,
  getFormStateFromLink,
} from '@/core/components/profile/modules/socials-edit-form';
import { SocialPlatformIcon } from '@/core/components/profile/modules/socials-platform-icon';
import {
  SortableSocialItem,
  socialSortableId,
} from '@/core/components/profile/modules/socials-sortable-item';
import { SOCIAL_PLATFORMS } from '@/core/components/profile/modules/socials-shared';
import type { EditFormState } from '@/core/components/profile/modules/socials-edit-form';
import { SortableSocialEditSlot } from '@/core/components/profile/modules/socials-sortable-item';

interface SocialsModuleConfigProps extends ModuleEntryGovernanceProps {
  config: SocialsModuleConfig;
  onChange: (config: SocialsModuleConfig) => void;
  fieldLockedHintMessage?: string;
}

function reorderLinks(
  links: SocialLink[],
  fromIndex: number,
  toIndex: number,
): SocialLink[] {
  const result = [...links];
  const [removed] = result.splice(fromIndex, 1);
  if (!removed) return result;
  result.splice(toIndex, 0, removed);
  return result;
}

/** -1 = adding new, 0+ = editing that index, null = idle */
type EditingIndex = number | null;

export function SocialsModuleConfigComponent({
  config,
  onChange,
  structureReadOnly = false,
  templateModule,
  showEntryPolicies = false,
  onEntryPolicyChange,
  policyLabels,
  fieldLockedHintMessage,
  showEntryOverrideHints = false,
  templateOverrideLabels,
  overrideHintMessage,
  contentReadOnly = false,
  workspaceId,
  templateId,
  publishStatus,

  memberRoster,
  memberRosterLoading,
  hasAssignedTeams,
  memberValuesDict,
}: SocialsModuleConfigProps) {
  const isMobile = useIsMobile();
  const [localConfig, setLocalConfig] = useState<SocialsModuleConfig>(config);
  const [prevConfig, setPrevConfig] = useState(config);
  if (config !== prevConfig) {
    setPrevConfig(config);
    setLocalConfig(config);
  }
  const [editingIndex, setEditingIndex] = useState<EditingIndex>(null);
  const [formState, setFormState] = useState<EditFormState>(getEmptyFormState);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  );
  const [customIconPickerList, setCustomIconPickerList] =
    useState<IconList | null>(null);

  useEffect(() => {
    let cancelled = false;

    if (formState.platform !== 'custom') return;
    if (customIconPickerList) return;

    void (async () => {
      const list = await getSocialCustomIconPickerList();
      if (cancelled) return;
      setCustomIconPickerList(list);
    })();

    return () => {
      cancelled = true;
    };
  }, [formState.platform, customIconPickerList]);

  const links = localConfig.links || [];

  const handleChange = (updates: Partial<SocialsModuleConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  };

  const currentModuleDto = useMemo(
    (): ProfileModuleDto => ({
      type: ProfileModuleType.SOCIALS,
      order: 0,
      enabled: true,
      config: localConfig as unknown as Record<string, unknown>,
    }),
    [localConfig],
  );

  const resetEntryToTemplate = (entryId: string) => {
    const templateLinks = (templateModule?.config?.links as SocialLink[]) ?? [];
    const templateEntry = templateLinks.find((entry) => entry.id === entryId);
    if (!templateEntry) return;
    const { policy: _policy, ...templateData } = templateEntry;
    handleChange({
      links: links.map((link) =>
        link.id === entryId ? { ...templateData, id: entryId } : link,
      ),
    });
  };

  const startAdd = () => {
    setEditingIndex(-1);
    setFormState(getEmptyFormState());
  };

  const startEdit = (index: number) => {
    const link = links[index];
    if (!link) return;
    setEditingIndex(index);
    setFormState(getFormStateFromLink(link));
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setFormState(getEmptyFormState());
  };

  const saveEdit = () => {
    if (editingIndex == null) return;
    const url = getSocialUrl(formState.platform, formState.username);
    if (!url) {
      setFormState({
        ...formState,
        urlError:
          formState.platform === 'custom'
            ? 'Bitte URL eingeben.'
            : 'Bitte Benutzername oder URL eingeben.',
      });
      return;
    }
    if (
      !isValidSocialHandle(
        formState.platform,
        normalizeSocialHandle(formState.platform, formState.username),
      )
    ) {
      setFormState({
        ...formState,
        urlError:
          'Ungültiger Benutzername. Erlaubt sind Buchstaben, Ziffern, Punkt, Unterstrich und Bindestrich – oder füge die Profil-URL ein.',
      });
      return;
    }
    const existing = editingIndex >= 0 ? links[editingIndex] : undefined;
    const entryId = existing?.id ?? crypto.randomUUID();
    const newLink: SocialLink = {
      id: entryId,
      platform: formState.platform,
      url,
      label: formState.label.trim() || undefined,
      icon: formState.platform === 'custom' ? formState.customIcon : undefined,
      ...(existing?.policy != null
        ? { policy: existing.policy }
        : showEntryPolicies
          ? { policy: defaultPolicyForNewEntry() }
          : {}),
    };
    if (editingIndex === -1) {
      handleChange({ links: [...links, newLink] });
    } else {
      const newLinks = [...links];
      newLinks[editingIndex] = { ...newLinks[editingIndex], ...newLink };
      handleChange({ links: newLinks });
    }
    setEditingIndex(null);
    setFormState(getEmptyFormState());
  };

  const requestRemove = (index: number) => {
    setPendingRemoveIndex(index);
  };

  const confirmRemove = () => {
    if (pendingRemoveIndex == null) return;
    const index = pendingRemoveIndex;
    const newLinks = links.filter((_, i) => i !== index);
    handleChange({ links: newLinks });
    if (editingIndex === index) {
      setEditingIndex(null);
    } else if (
      editingIndex != null &&
      editingIndex >= 0 &&
      editingIndex > index
    ) {
      setEditingIndex(editingIndex - 1);
    }
    setPendingRemoveIndex(null);
  };

  const lastOverIdRef = useRef<string | null>(null);

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (over == null) {
      lastOverIdRef.current = null;
      return;
    }
    const isNewSlot = lastOverIdRef.current !== over.id;
    const isBackToOwnSlot =
      over.id === active.id && lastOverIdRef.current === null;
    if (isNewSlot && !isBackToOwnSlot) {
      void triggerDragOverHaptic();
    }
    lastOverIdRef.current = over.id != null ? String(over.id) : null;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (structureReadOnly) return;
    const { active, over } = event;
    if (over == null || active.id === over.id) return;
    const fromIndex = links.findIndex(
      (link, i) => socialSortableId(link, i) === active.id,
    );
    const toIndex = links.findIndex(
      (link, i) => socialSortableId(link, i) === over.id,
    );
    if (fromIndex === -1 || toIndex === -1) return;
    const reordered = reorderLinks(links, fromIndex, toIndex);
    handleChange({ links: reordered });
    if (editingIndex !== null && editingIndex >= 0) {
      if (editingIndex === fromIndex) {
        setEditingIndex(toIndex);
      } else if (fromIndex < editingIndex && toIndex >= editingIndex) {
        setEditingIndex(editingIndex - 1);
      } else if (fromIndex > editingIndex && toIndex <= editingIndex) {
        setEditingIndex(editingIndex + 1);
      }
    }
  };

  const sensors = useDndProfileSensors();

  const isAdding = editingIndex === -1;
  const isEditingExisting = editingIndex != null && editingIndex >= 0;

  const pendingRemoveLink =
    pendingRemoveIndex != null ? links[pendingRemoveIndex] : undefined;
  const pendingRemoveLabel = useMemo(() => {
    if (!pendingRemoveLink) return '';
    const custom = pendingRemoveLink.label?.trim();
    const username = getSocialUsername(
      pendingRemoveLink.platform,
      pendingRemoveLink.url,
    );
    return (
      custom ||
      username ||
      SOCIAL_PLATFORMS.find((p) => p.value === pendingRemoveLink.platform)
        ?.label ||
      pendingRemoveLink.platform
    );
  }, [pendingRemoveLink]);

  const editCardTitle = isAdding
    ? 'Social Link hinzufügen'
    : `${SOCIAL_PLATFORMS.find((p) => p.value === formState.platform)?.label ?? formState.platform} bearbeiten`;

  const editCardIcon = isAdding ? null : (
    <SocialPlatformIcon platform={formState.platform} className="h-5 w-5" />
  );

  const inlineEditCard =
    editingIndex !== null ? (
      <InlineEditCard
        variant="bare"
        asSheet={isMobile}
        title={editCardTitle}
        icon={isMobile ? undefined : editCardIcon}
        onSave={saveEdit}
        onCancel={cancelEdit}
      >
        <SocialEditForm
          state={formState}
          onChange={setFormState}
          customIconPickerList={customIconPickerList}
          contentReadOnly={contentReadOnly}
        />
      </InlineEditCard>
    ) : null;

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label>Social Links</Label>
        {links.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={() => {
              lastOverIdRef.current = null;
              lockBodyScrollDuringDrag();
              void triggerDragStartHaptic();
            }}
            onDragOver={handleDragOver}
            onDragEnd={(e) => {
              handleDragEnd(e);
              unlockBodyScrollAfterDrag();
              void triggerDragEndHaptic();
            }}
          >
            <SortableContext
              items={links.map((link, i) => socialSortableId(link, i))}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {links.map((link, index) => {
                  const entryId = link.id ?? String(index);
                  const sortableId = socialSortableId(link, index);
                  return isEditingExisting &&
                    editingIndex === index &&
                    !isMobile ? (
                    <SortableSocialEditSlot
                      key={sortableId}
                      sortableId={sortableId}
                    >
                      {inlineEditCard}
                    </SortableSocialEditSlot>
                  ) : (
                    <SortableSocialItem
                      key={sortableId}
                      link={link}
                      index={index}
                      sortableId={sortableId}
                      structureReadOnly={structureReadOnly}
                      entryLocked={
                        templateModule
                          ? isEntryPolicyLocked(templateModule, entryId)
                          : false
                      }
                      showEntryPolicy={showEntryPolicies}
                      entryPolicy={
                        templateModule
                          ? resolveEntryPolicy(templateModule, entryId)
                          : undefined
                      }
                      onEntryPolicyChange={
                        onEntryPolicyChange
                          ? (mode) => onEntryPolicyChange(entryId, mode)
                          : undefined
                      }
                      policyLabels={policyLabels}
                      fieldLockedHintMessage={fieldLockedHintMessage}
                      showEntryOverrideHints={showEntryOverrideHints}
                      currentModule={currentModuleDto}
                      templateModule={templateModule}
                      entryId={entryId}
                      overrideHintMessage={overrideHintMessage}
                      templateOverrideLabels={templateOverrideLabels}
                      onResetEntry={() => resetEntryToTemplate(entryId)}
                      onEdit={() => startEdit(index)}
                      onRemove={() => requestRemove(index)}
                      contentReadOnly={contentReadOnly}
                      workspaceId={workspaceId}
                      templateId={templateId}
                      publishStatus={publishStatus}
                      memberRoster={memberRoster}
                      memberRosterLoading={memberRosterLoading}
                      hasAssignedTeams={hasAssignedTeams}
                      memberValuesDict={memberValuesDict}
                    />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        ) : null}

        {isAdding && !isMobile ? (
          inlineEditCard
        ) : !structureReadOnly ? (
          <button
            type="button"
            onClick={startAdd}
            disabled={isEditingExisting}
            className={cn(
              'group flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/30 px-4 py-5',
              'text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary',
              'disabled:opacity-50 disabled:pointer-events-none',
            )}
            aria-label="Social Link hinzufügen"
          >
            <Plus className="h-5 w-5" />
            <span className="font-medium">Social Link hinzufügen</span>
          </button>
        ) : null}
        {isMobile ? inlineEditCard : null}
      </div>

      <ConfirmDialog
        open={pendingRemoveIndex !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRemoveIndex(null);
        }}
        title="Social Link entfernen?"
        description={
          pendingRemoveLabel ? (
            <>
              Möchtest du{' '}
              <span className="font-medium">{pendingRemoveLabel}</span> wirklich
              entfernen? Diese Aktion kann nicht rückgängig gemacht werden.
            </>
          ) : (
            'Möchtest du diesen Link wirklich entfernen? Diese Aktion kann nicht rückgängig gemacht werden.'
          )
        }
        confirmLabel="Entfernen"
        cancelLabel="Abbrechen"
        destructive
        onConfirm={confirmRemove}
      />
    </div>
  );
}
